import { colors } from '@/theme'
import { ArrowBack } from '@mui/icons-material'
import { Box } from '@mui/material'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface BackButtonState {
  backLabel?: ReactNode
  fallbackPath?: string
}

interface BackButtonProps {
  onClick?: () => void
  fallbackPath?: string
  defaultLabel?: ReactNode
  children?: ReactNode
}

export default function BackButton({ onClick, fallbackPath = '/', defaultLabel, children }: BackButtonProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as BackButtonState | null
  const resolvedFallbackPath = state?.fallbackPath ?? fallbackPath
  const resolvedLabel = children ?? state?.backLabel ?? defaultLabel

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }

    const historyState = window.history.state as { idx?: number } | null
    const hasInAppBackEntry = typeof historyState?.idx === 'number' && historyState.idx > 0

    if (hasInAppBackEntry) {
      navigate(-1)
      return
    }

    navigate(resolvedFallbackPath, { replace: true })
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: children ? 2 : 0,
        cursor: 'pointer',
        color: colors.brand.primary,
        '&:hover': { textDecoration: 'underline' }
      }}
    >
      <ArrowBack sx={{ fontSize: 16 }} />
      {resolvedLabel}
    </Box>
  )
}
